/**
 * Shared style primitives (plain objects for inline `style={}`).
 * Composes legacy `foundation` (semantic-backed) — no blur.
 */

import { foundation as f } from './tokens';
import { bg as semanticBg, elevation as semanticElevation, nav as semanticNav, transition as semanticTransition } from './theme/semanticTokens';

const { color, spacePx, radii, shadow, fontSize, fontWeight, lineHeight, fontFamily } = f;

/** Full-viewport app background — height owned by AppShell (fixed/inset:0) */
export const pageShell = {
  height: '100%',
  width: '100%',
  backgroundColor: color.neutral.bg,
  color: color.neutral.text,
  fontFamily: fontFamily.sans,
  fontSize: fontSize.md,
  lineHeight: lineHeight.normal,
  boxSizing: 'border-box',
};

/** Constrained content column (optional max-width) */
export const pageContent = {
  maxWidth: '1400px',
  margin: '0 auto',
  width: '100%',
  padding: `${spacePx[6]} ${spacePx[4]}`,
  boxSizing: 'border-box',
};

/** Default card / panel */
export const surface = {
  backgroundColor: color.neutral.surface,
  borderRadius: radii.lg,
  border: `1px solid ${color.neutral.border}`,
  boxShadow: shadow.xs,
  transition: semanticTransition.normal,
};

export const surfaceElevated = {
  ...surface,
  boxShadow: shadow.sm,
  borderColor: color.neutral.borderStrong,
  transition: semanticTransition.normal,
};

/** JS-driven hover: merge `_restStyle` as base, swap toward `_hoverStyle` on pointer enter */
export const surfaceInteractive = {
  _restStyle: {
    backgroundColor: color.neutral.surface,
    borderRadius: radii.lg,
    border: `1px solid ${color.neutral.border}`,
    boxShadow: shadow.xs,
    transition: semanticTransition.normal,
  },
  _hoverStyle: {
    boxShadow: semanticElevation.elevated,
    transform: 'translateY(-1px)',
  },
};

/**
 * Primary action button — CrewVex brand-navy.
 * Used everywhere a user commits/submits/sends (Enviar Trabajo, Registrar Trabajo,
 * Save, Send Invoice, Apply, etc.). Solid brand-primary fill, white text.
 *
 * NOTE: keep this as a SOLID brand-primary color (no gradient). Dark-slate
 * gradients are reserved for "headline cards" (PENDIENTE hero, day header) —
 * never for buttons. One color, one shape, consistent across the app.
 */
export const buttonPrimary = {
  fontFamily: fontFamily.sans,
  fontSize: fontSize.sm,
  fontWeight: fontWeight.semibold,
  lineHeight: lineHeight.tight,
  padding: `${spacePx[2]} ${spacePx[4]}`,
  borderRadius: radii.md,
  border: `1px solid ${color.brand.primary}`,
  background: color.brand.primary,
  color: color.brand.onPrimary,
  cursor: 'pointer',
  boxShadow: shadow.xs,
};

export const buttonSecondary = {
  fontFamily: fontFamily.sans,
  fontSize: fontSize.sm,
  fontWeight: fontWeight.medium,
  lineHeight: lineHeight.tight,
  padding: `${spacePx[2]} ${spacePx[4]}`,
  borderRadius: radii.md,
  border: `1px solid ${color.neutral.borderStrong}`,
  backgroundColor: color.neutral.surface,
  color: color.brand.text,
  cursor: 'pointer',
};

export const buttonGhost = {
  fontFamily: fontFamily.sans,
  fontSize: fontSize.sm,
  fontWeight: fontWeight.medium,
  lineHeight: lineHeight.tight,
  padding: `${spacePx[2]} ${spacePx[3]}`,
  borderRadius: radii.md,
  border: '1px solid transparent',
  backgroundColor: 'transparent',
  color: color.brand.text,
  cursor: 'pointer',
};

export const buttonDanger = {
  ...buttonPrimary,
  border: `1px solid ${color.semantic.danger}`,
  // `background` shorthand overrides buttonPrimary's linear-gradient. Setting
  // only backgroundColor (longhand) leaves the gradient visible — that bug
  // shipped "Turn off 2FA" as navy with a red border.
  background: color.semantic.dangerFill,
  color: color.brand.onPrimary,
};

export const tabsRow = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: spacePx[2],
  rowGap: spacePx[2],
  marginBottom: spacePx[3],
  paddingBottom: spacePx[2],
  borderBottom: `1px solid ${color.neutral.border}`,
};

export const tab = {
  fontFamily: fontFamily.sans,
  fontSize: fontSize.sm,
  fontWeight: fontWeight.semibold,
  padding: `${spacePx[2]} ${spacePx[3]}`,
  borderRadius: radii.md,
  border: '1px solid transparent',
  background: 'transparent',
  color: color.neutral.textMuted,
  cursor: 'pointer',
  outline: 'none',
  WebkitTapHighlightColor: 'transparent',
};

export const tabActive = {
  color: color.brand.text,
  backgroundColor: color.neutral.surfaceMuted,
  border: `1px solid ${color.brand.primary}`,
};

export { filterBar } from './dataSurfaceStyles';

export const filterInput = {
  fontFamily: fontFamily.sans,
  fontSize: fontSize.sm,
  padding: `${spacePx[2]} ${spacePx[3]}`,
  borderRadius: radii.md,
  border: `1px solid ${color.neutral.borderField}`,
  backgroundColor: color.neutral.surface,
  color: color.neutral.text,
  minWidth: '160px',
  boxSizing: 'border-box',
  transition: semanticTransition.fast,
};

export const field = {
  fontFamily: fontFamily.sans,
  fontSize: fontSize.sm,
  padding: `${spacePx[2]} ${spacePx[3]}`,
  borderRadius: radii.md,
  border: `1px solid ${color.neutral.borderField}`,
  backgroundColor: color.neutral.surface,
  color: color.neutral.text,
  width: '100%',
  boxSizing: 'border-box',
  transition: semanticTransition.fast,
  // Kill WebKit's default black focus rim (RD-UI-036); focus color is CSS.
  appearance: 'none',
  WebkitAppearance: 'none',
  outline: 'none',
  WebkitTapHighlightColor: 'transparent',
};

export const textarea = {
  ...field,
  minHeight: '96px',
  resize: 'vertical',
  lineHeight: lineHeight.relaxed,
};

/** Custom dropdown arrow SVG (slate-500, 12×12 chevron) */
const selectArrowSvg =
  'url("data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 width=%2712%27 height=%2712%27 viewBox=%270 0 12 12%27%3E%3Cpath fill=%27%2364748b%27 d=%27M2.5 4L6 7.5L9.5 4%27/%3E%3C/svg%3E")';

/** Base select — consistent arrow, appearance reset, pointer cursor */
export const select = {
  ...field,
  cursor: 'pointer',
  WebkitAppearance: 'none',
  MozAppearance: 'none',
  appearance: 'none',
  paddingRight: '36px',
  backgroundImage: selectArrowSvg,
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right 12px center',
  backgroundSize: '12px',
};

/** Compact select for toolbars / filter bars */
export const selectCompact = {
  ...select,
  minWidth: 0,
  minHeight: '38px',
  boxShadow: semanticElevation.fieldInset,
};

export {
  modalScrimCentered as modalOverlay,
  modalDialogPanel as modalShell,
  drawerSheetRight as drawerShell,
} from './overlayStyles';

/** Light left navigation rail (desktop) — calm surface, hairline edge */
export const navRailSurface = {
  // Menu refresh (KAN-780): the rail sits directly on the soft grey app frame
  // (AppShell paints it) — no surface of its own, no right border.
  backgroundColor: 'transparent',
  boxShadow: 'none',
};

export const navRailHeader = {
  borderBottom: `1px solid ${color.neutral.border}`,
};

export const navRailTitle = {
  color: color.neutral.textSecondary,
  fontFamily: fontFamily.sans,
  fontWeight: fontWeight.semibold,
  letterSpacing: '0.02em',
};

export const navRailCloseButton = {
  border: `1px solid ${color.neutral.border}`,
  backgroundColor: 'transparent',
  color: color.neutral.textMuted,
  borderRadius: radii.md,
  boxShadow: 'none',
};

export const navRailItemInactive = {
  color: color.neutral.textSecondary,
  backgroundColor: 'transparent',
  borderColor: 'transparent',
};

export const navRailItemHover = {
  backgroundColor: semanticBg.tintHover,
  color: color.neutral.text,
  borderColor: 'transparent',
};

/** The ONE selected-card look for every nav surface (rail, drawer, dock, phone chip). */
export const navSelectedCard = {
  backgroundColor: semanticNav.selectedCard,
  boxShadow: semanticNav.selectedShadow,
};

/** Desktop rail selection: the gliding `navSelectedCard` span paints it, the row stays clear. */
export const navRailItemActive = {
  backgroundColor: 'transparent',
  color: color.neutral.text,
  borderColor: 'transparent',
  boxShadow: 'none',
};

/** Icon wells stay quiet — no filled circles */
export const navRailIcon = {
  backgroundColor: 'transparent',
};

export const navRailIconActive = {
  backgroundColor: 'transparent',
};

/** @deprecated Use navRailSurface — light rail only */
export const navRailDark = navRailSurface;

export const chip = {
  display: 'inline-flex',
  alignItems: 'center',
  fontFamily: fontFamily.sans,
  fontSize: fontSize.xs,
  fontWeight: fontWeight.medium,
  lineHeight: lineHeight.tight,
  padding: `${spacePx[1]} ${spacePx[2]}`,
  borderRadius: radii.full,
  border: `1px solid ${color.neutral.border}`,
  backgroundColor: color.neutral.surfaceMuted,
  color: color.neutral.textSecondary,
};

export const badge = {
  display: 'inline-flex',
  alignItems: 'center',
  whiteSpace: 'nowrap',
  fontFamily: fontFamily.sans,
  fontSize: fontSize.xs,
  fontWeight: fontWeight.semibold,
  lineHeight: lineHeight.tight,
  padding: `${spacePx[1]} ${spacePx[2]}`,
  borderRadius: radii.full,
  backgroundColor: color.brand.primaryMuted,
  color: color.brand.text,
};

export const mono = {
  fontFamily: fontFamily.mono,
  fontSize: fontSize.xs,
};

/** @deprecated Use buttonPrimary instead */
export const buttonNavy = { ...buttonPrimary };

/* ── Status badge variants (soft chip style) ── */

export const badgeSuccess = {
  ...badge,
  backgroundColor: color.semantic.successSoft,
  color: color.semantic.success,
  border: `1px solid ${color.semantic.successBorder}`,
};

export const badgeWarning = {
  ...badge,
  backgroundColor: color.semantic.warningSoft,
  color: color.semantic.warningText,
  border: `1px solid ${color.semantic.warningBorder}`,
};

export const badgeDanger = {
  ...badge,
  backgroundColor: color.semantic.dangerSoft,
  color: color.semantic.danger,
  border: `1px solid ${color.semantic.dangerBorder}`,
};

export const badgeInfo = {
  ...badge,
  backgroundColor: color.semantic.infoSoft,
  color: color.semantic.info,
  border: `1px solid ${color.semantic.infoBorder}`,
};

export const badgeNeutral = {
  ...badge,
  backgroundColor: color.neutral.surfaceMuted,
  color: color.neutral.textSecondary,
  border: `1px solid ${color.neutral.border}`,
};

/* ── Action button variants (within table rows) ── */

export const buttonDangerOutline = {
  ...buttonSecondary,
  color: color.semantic.danger,
  borderColor: color.semantic.dangerBorder,
};

export const buttonAccentOutline = {
  ...buttonSecondary,
  color: color.brand.text,
  borderColor: color.brand.primaryMuted,
};

export const buttonWarningOutline = {
  ...buttonSecondary,
  color: color.semantic.warningText,
  borderColor: color.semantic.warningBorder,
};

export const buttonSuccessOutline = {
  ...buttonSecondary,
  color: color.semantic.success,
  borderColor: color.semantic.successBorder,
};
